import React, { Dispatch, SetStateAction } from 'react'

import SandDollarIcon from '@src/components/icons/SandDollar'
import SearchIcon from '@src/components/icons/Search'
import { copy } from '@src/constants/application'
import { ConfigLoadingStatus } from '@src/enums'
import { useFetchAllTokens } from '@src/hooks/useFetchAllTokens'
import {
    ChainSelector,
    SearchingIndicator,
    TokenCategoriesDisplay,
    TokenResultsDisplay,
} from '@src/pages/Exchange/components/TokenSelectorModal/components'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils'
import { useConfigStore } from '@src/zustand_stores'

interface DesktopTokenSelectorProps {
    tokenQuery: string
    setTokenQuery: Dispatch<SetStateAction<string>>
    searchResultsChain: Record<string, any>
    availableChainsFiltered: Record<string, any>
    selectedChain: string | number
    temporaryChainSelection?: number
    activeChainSelection?: number
    onChainClicked: (chainId: number) => void
    handlePasteAddress: () => void
    handleClearSearch: () => void
    onClose: () => void
    isSearchingAllChains: boolean
    hasNoSearchResults: boolean
    renderNoResultsFound: () => JSX.Element
    searchProgress: { completed: number; total: number }
    displayedTokens: IToken[]
    selectedToken?: IToken
    onTokenClicked: (token: IToken) => void
    getTokenBalance: (token: IToken) => { tokenBalance: string; usdBalance: string } | undefined
    searchResult: IToken[]
    side: 'buy' | 'sell'
    sellData: IExchangeData
    buyData: IExchangeData
    highlightedChain?: number
    disabledChains?: number[]
    onTokenSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
}

const DesktopTokenSelector = ({
    tokenQuery,
    setTokenQuery,
    searchResultsChain,
    availableChainsFiltered,
    selectedChain,
    temporaryChainSelection,
    activeChainSelection,
    onChainClicked,
    handlePasteAddress,
    handleClearSearch,
    onClose,
    isSearchingAllChains,
    hasNoSearchResults,
    renderNoResultsFound,
    searchProgress,
    displayedTokens,
    selectedToken,
    onTokenClicked,
    getTokenBalance,
    searchResult,
    side,
    sellData,
    buyData,
    highlightedChain,
    disabledChains,
    onTokenSearchChange,
}: DesktopTokenSelectorProps): JSX.Element => {
    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading } = useFetchAllTokens()

    const isConfigurationLoading = React.useMemo(
        () =>
            configLoading === ConfigLoadingStatus.loading_not_yet_started ||
            configLoading === ConfigLoadingStatus.loading ||
            zustandTokensLoading,
        [configLoading, zustandTokensLoading]
    )
    return (
        <div className='hidden h-full flex-col sm:flex'>
            {/* Header with title and close button */}
            <div className='flex items-center justify-between p-4'>
                <h2>Select {side} token</h2>
                <button
                    onClick={onClose}
                    className='rounded-[6px] bg-mach-gray p-0.5 text-white transition-colors hover:brightness-110'
                    type='button'
                >
                    <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='scale-75 transform'
                    >
                        <path
                            d='M18 6L6 18M6 6L18 18'
                            stroke='white'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                </button>
            </div>

            {/* Full Width Search Bar */}
            <div className='px-4'>
                <div className='flex items-center justify-between gap-3 rounded-md bg-mach-darkBlue py-1.5 pl-3 pr-1.5 transition-all duration-200'>
                    <div className='flex flex-1 items-center gap-2'>
                        <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                        <input
                            type='text'
                            placeholder={
                                isConfigurationLoading ? 'Loading Configuration...' : copy.tokenSelectorPlaceholder
                            }
                            value={tokenQuery}
                            onChange={onTokenSearchChange}
                            disabled={isConfigurationLoading}
                            className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none disabled:cursor-not-allowed disabled:opacity-50'
                            autoFocus
                        />
                    </div>
                    {tokenQuery ? (
                        <button
                            className='rounded-md bg-mach-gray p-1.5 text-sm !leading-4 text-white transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={handleClearSearch}
                            disabled={isConfigurationLoading}
                        >
                            Clear
                        </button>
                    ) : (
                        <button
                            className='rounded-md bg-mach-gray p-1.5 text-sm !leading-4 text-white transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={handlePasteAddress}
                            disabled={isConfigurationLoading}
                        >
                            Paste address
                        </button>
                    )}
                </div>
            </div>

            {/* Show searching indicator, no results UI, or the column layout */}
            {isSearchingAllChains ? (
                <div className='flex-1'>
                    <SearchingIndicator searchProgress={searchProgress} />
                </div>
            ) : hasNoSearchResults ? (
                renderNoResultsFound()
            ) : (
                <>
                    {/* Two-column layout for Networks and Tokens */}
                    <div className='flex flex-1 overflow-hidden'>
                        {/* Networks Column */}
                        <div className='w-[192px] overflow-auto'>
                            <div className='p-4 pb-2'>
                                <h3 className='text-sm text-token-modal-label-text'>
                                    {(() => {
                                        const networkCount = tokenQuery
                                            ? Object.keys(searchResultsChain).length
                                            : Object.keys(availableChainsFiltered).length
                                        return networkCount === 1 ? 'Network' : `Networks (${networkCount})`
                                    })()}
                                </h3>
                            </div>
                            <div className='space-y-2 p-4 pt-0'>
                                <ChainSelector
                                    chains={tokenQuery ? searchResultsChain : availableChainsFiltered}
                                    selectedChain={selectedChain}
                                    onChainClicked={onChainClicked}
                                    disabledChains={disabledChains}
                                    highlightedChain={highlightedChain}
                                    tokenQuery={tokenQuery}
                                    activeChainSelection={temporaryChainSelection ?? activeChainSelection}
                                    isMobile={false}
                                />
                            </div>
                        </div>

                        {/* Tokens Column */}
                        <div className='flex-1 overflow-auto'>
                            <div className='p-4'>
                                {tokenQuery ? (
                                    <TokenResultsDisplay
                                        activeChainSelection={activeChainSelection}
                                        tokenQuery={tokenQuery}
                                        availableChains={availableChainsFiltered}
                                        displayedTokens={displayedTokens}
                                        getTokenBalance={getTokenBalance}
                                        selectedToken={selectedToken}
                                        onTokenClicked={onTokenClicked}
                                        searchResult={searchResult}
                                        currentlySelectedToken={side === 'sell' ? sellData.token : buyData.token}
                                    />
                                ) : (
                                    <TokenCategoriesDisplay
                                        displayedTokens={displayedTokens}
                                        selectedToken={selectedToken}
                                        onTokenClicked={onTokenClicked}
                                        getTokenBalance={getTokenBalance}
                                        currentlySelectedToken={side === 'sell' ? sellData.token : buyData.token}
                                        temporaryChainSelection={temporaryChainSelection}
                                        activeChainSelection={activeChainSelection}
                                        selectedChain={selectedChain}
                                        tokenQuery={tokenQuery}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
            {/* Sand Dollars Banner */}
            {isFeatureEnabled('SAND_DOLLARS') && (
                <div className='p-4'>
                    <div className='flex items-center gap-2 rounded-[10px] bg-[#381601] p-2'>
                        <SandDollarIcon height='32' width='32' />
                        <p className='flex-1 text-xs font-medium leading-4 text-[#E4E4E4]'>
                            Earn Sand Dollars by making non duo-stablecoin swaps and receive your cashback in stablecoin
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DesktopTokenSelector
